(ns graph-ui.web.busy
  "What is the client STILL WAITING FOR? — the in-flight half of the picture
   graph-ui.web.activity already draws for finished work.

   George, 2026-08-17: *\"Is it possible to introduce some UI indication that
   click is accepted and will take time to be accomplished — like hour glass, or
   top bar indication of progress?\"* It is, and the numbers say it is worth it:
   one `:frames-lib/list-frames` against the live graph measured **337 ms**, and
   one click is not one round trip. Clicking a page in the reader dispatches the
   gesture, the server emits :ui/source-changed for every source that draws the
   pane, and graph-ui.web.core/on-source-changed answers each one with its OWN
   :ui/read. So the wait a reader feels is a CASCADE of round trips, and until
   now nothing on screen said one was running.

   WHY THIS CANNOT BE A BUS :source, though every other value the UI shows is
   one. A bound source is read BY dispatching :ui/read — the very round trip this
   namespace exists to report on. 'Am I waiting for the server?' answered by
   asking the server is a question that arrives with its own answer already
   stale, and during a disconnect it cannot be answered at all. So this is the
   graph-ui.web.activity / graph-ui.web.draft / graph-ui.web.locate pattern: a
   reagent atom the widget derefs DIRECTLY. Same reasoning activity.cljs states
   in its own docstring — 'a server source could never see the latter'.

   graph-ui.web.bus-client owns the truth already: `pending-atom` maps envelope
   id -> reply callback, and it is exact. It was a plain atom, so nothing could
   re-render off it, and the count was discarded when the reply landed. This
   namespace is that same fact made REACTIVE and given a subject.

   TWO SURFACES, TWO DIFFERENT CLAIMS — the split matters, because an indicator
   that overstates what it knows is worse than none:

   - `waiting-on?` — the hourglass on the row you clicked. Fires with NO delay,
     because acknowledgement is the whole point, and it claims only what it can
     prove: a request naming this thing is outstanding.
   - `state` — the top-bar progress line. Waits `visible-after-ms` so a routine
     read does not strobe the chrome, and covers the WHOLE cascade, because
     every :ui/read in it passes through the same client.

   WHAT NEITHER CLAIMS: that the CONTENT has arrived. The follow-up
   :ui/source-changed carries `{:session-id …}` and nothing else (see
   ui.notes-web/refresh-selection-sources!), so no read in the cascade names
   the page it is for.
   A row therefore cannot honestly say 'your page is ready' — only 'a request
   about this is still out'. `session-waiting?` is the closest available truth
   and is scoped by the one key those events DO carry."
  (:require [reagent.core :as r]
            [clojure.string :as string]))

;; @feature F-graph-ui-v0-001-01-ui-quality-system
;; @concept CONCEPT-ikc-v3-003-01-observability-surface

;; ── the thresholds, and the measurements behind them ────────────────────────
;;
;; Not tokens. themes.edn names what a THEME may restate — colour, type, motion,
;; state — and a latency threshold is none of those: re-skinning the canvas must
;; not change when the product admits it is slow. They are named here, next to
;; the measurement that chose them, which is the other half of "findable".

(def visible-after-ms
  "How long the client must be waiting before the top bar appears.
   400ms, chosen from a measurement rather than taste: a routine
   :frames-lib/list-frames on the live graph took 337 ms (2026-08-17), so a
   threshold under that would put a flashing bar on top of ordinary reading. Above
   it, the bar means what it says — this one is taking longer than usual."
  400)

(def slow-after-ms
  "When waiting stops being normal and becomes worth naming. 6s. The reason this
   number is not larger: the cascade this namespace was built for was measured at
   30-40s (the island-select hunt, activity.cljs), and a reader who has waited six
   seconds with no explanation has already concluded the click was dropped. Past
   this the bar shifts to --warn and shows the elapsed seconds, so a stall reads as
   a stall instead of as a hang."
  6000)

(def ^:private tick-ms
  "How often to re-render while waiting. 100ms — fast enough that crossing
   visible-after-ms looks immediate, slow enough to be free. Needed because both
   thresholds are about the PASSAGE of time: with no ticker, a request that never
   replies would leave the bar hidden forever, having never re-rendered after the
   moment it started."
  100)

;; ── state ───────────────────────────────────────────────────────────────────

(defonce ^:private pending
  ;; envelope id -> {:at :type :source :session :subjects}
  (r/atom {}))

(defonce ^:private clock
  ;; Bumped by the ticker purely to invalidate reagent renders that depend on
  ;; elapsed time. Its value carries no meaning; only its CHANGING does.
  (r/atom 0))

(defonce ^:private ticker (atom nil))

(defn- stop-ticker! []
  (when-let [t @ticker]
    (js/clearInterval t)
    (reset! ticker nil)))

(defn- ensure-ticker!
  "Run the tick clock only while something is outstanding. An always-on interval
   would re-render the chrome bar forever on an idle canvas."
  []
  (when (nil? @ticker)
    (reset! ticker (js/setInterval (fn []
                                     (if (empty? @pending)
                                       (stop-ticker!)
                                       (swap! clock inc)))
                                   tick-ms))))

(defn- subjects
  "Every scalar in `command` (and in its :args), as strings — the set of things
   this request is ABOUT, so a row can ask whether it is one of them.

   :type and :session-id are excluded deliberately. A command's type is not its
   subject, and EVERY command carries the session id, so leaving it in would make
   `waiting-on?` true for everything the moment anything was in flight."
  [command]
  (let [scalar? #(or (string? %) (keyword? %) (number? %))]
    (into #{}
          (comp (remove (fn [[k _]] (contains? #{:type :session-id} k)))
                (mapcat (fn [[_ v]] (if (map? v) (vals v) [v])))
                (filter scalar?)
                (map str))
          command)))

(defn start!
  "Record that envelope `id` carrying `command` has gone out and has not been
   answered. Called from graph-ui.web.bus-client at the moment of `.send` — and
   only then: registering a command the socket never actually sent would pin an
   hourglass on a row for the rest of the session."
  [id command at]
  (swap! pending assoc id
         {:at       at
          :type     (:type command)
          :source   (:source command)
          :session  (or (:session-id command) (get-in command [:args :session-id]))
          :subjects (subjects command)})
  (ensure-ticker!))

(defn finish!
  "Envelope `id` has been answered (or failed). Idempotent — a reply for an id
   this namespace never saw is a no-op, which is what makes it safe to call from
   the disconnect path as well as the reply path."
  [id]
  (swap! pending dissoc id)
  (when (empty? @pending) (stop-ticker!)))

(defn drop-all!
  "The socket closed: every outstanding request is now unanswerable. Called from
   bus-client's on-close, which already fails their callbacks — without this the
   indicator would keep claiming work was in progress on a dead connection, the
   exact dishonesty a fallback message committed once before (the four-day
   'Connecting to bus…' banner, core.cljs/attach!)."
  []
  (reset! pending {})
  (stop-ticker!))

;; ── pure readers (tested without timers or a socket) ────────────────────────

(defn oldest-at
  "The :at of the longest-outstanding entry, or nil when idle."
  [entries]
  (when (seq entries) (reduce min (map :at (vals entries)))))

(defn phase
  "How the wait should READ, given `entries` and the current `now`:

     :idle    — nothing outstanding.
     :warming — outstanding, but for less than visible-after-ms. Deliberately
                indistinguishable from :idle in the chrome: this is the state a
                normal 337ms read spends its whole life in.
     :busy    — worth showing.
     :slow    — worth naming, with its elapsed time.

   Pure, so the thresholds are unit-testable at exact boundaries rather than by
   sleeping and hoping."
  [entries now]
  (if-let [t0 (oldest-at entries)]
    (let [waited (- now t0)]
      (cond
        (>= waited slow-after-ms)    :slow
        (>= waited visible-after-ms) :busy
        :else                        :warming))
    :idle))

(defn label
  "One short human line for the indicator's tooltip: how many requests and what
   kind. Names the SOURCE for a :ui/read, because a pile of ':ui/read' is
   unattributable — the same gap activity.cljs closed with its :detail."
  [entries]
  (let [es (vals entries)
        n  (count es)]
    (when (pos? n)
      (let [names (->> es
                       (map (fn [{:keys [type source]}]
                              (if source (str source) (str type))))
                       distinct sort (take 4))]
        (str n " request" (when (> n 1) "s") " in flight: "
             (string/join ", " names)
             (when (> n 4) " …"))))))

;; ── reactive reads (deref inside a reagent render) ──────────────────────────

(defn state
  "Reactive snapshot for the top-bar indicator:
   `{:phase :idle|:warming|:busy|:slow, :count n, :waited ms, :label s}`.

   Derefs the tick clock as well as the pending map, which is what makes a render
   calling this re-run as the wait LENGTHENS and not only as requests come and go."
  []
  (let [entries @pending
        _       @clock
        now     (js/Date.now)
        t0      (oldest-at entries)]
    {:phase  (phase entries now)
     :count  (count entries)
     :waited (when t0 (- now t0))
     :label  (label entries)}))

(defn waiting-on?
  "Reactive: is a request naming `value` still outstanding? The hourglass on one
   row. Does NOT deref the clock — a row's acknowledgement has no threshold, so it
   needs no re-render as time passes, only when the request lands."
  [value]
  (let [needle (str value)]
    (boolean (some #(contains? (:subjects %) needle) (vals @pending)))))

(defn session-waiting?
  "Reactive: is any request for `session-id` outstanding? The widest claim that is
   still TRUE for the follow-up cascade, whose events carry the session id and
   nothing else (ui.notes-web/refresh-selection-sources!). This is what lets the row
   you clicked stay
   marked while the pane's own sources are being re-read, instead of going quiet
   the instant the gesture itself is acknowledged."
  [session-id]
  (let [sid (str session-id)]
    (boolean (some #(= sid (str (:session %))) (vals @pending)))))

(defn pending-count
  "Reactive count of outstanding requests — for tests and for the chrome bar's
   numeric badge."
  []
  (count @pending))

;; ── the row you clicked ─────────────────────────────────────────────────────
;;
;; `waiting-on?` alone under-reports, and the reason is worth stating because it
;; is the difference between an honest indicator and a reassuring one. The gesture
;; itself is answered FAST — the server records the selection and returns. What
;; takes the time is the cascade after it, and those :ui/read envelopes carry
;; `{:session-id …}` and nothing else (ui.notes-web/refresh-selection-sources!
;; emits exactly that),
;; so not one of them names the page it is fetching. A row that watched only its
;; own command would therefore stop spinning while the pane was still empty —
;; acknowledging the click and then abandoning it, which is worse than never
;; acknowledging it, because it says "done" when nothing has appeared.
;;
;; So one extra fact is kept: WHICH row the reader last clicked. Combined with
;; "is this session still waiting for anything", that is enough to hold the
;; hourglass for the whole visible wait without claiming knowledge the client does
;; not have. It is a single value, not a set: a second click supersedes the first,
;; which is also what the reader means by it.

(defonce ^:private latest-click (r/atom nil))   ; {:session :value :at}

(defn clicked!
  "Record that the reader just clicked `value` in `session-id`. Called from the
   gesture handler BEFORE the dispatch goes out, so the acknowledgement is on
   screen at the next frame rather than after the first round trip."
  [session-id value]
  (reset! latest-click {:session (str session-id) :value (str value) :at (js/Date.now)}))

(defn acknowledging?
  "Reactive: should `value` show an hourglass? True when it is the row this
   session last clicked AND that session still has something outstanding — either
   the gesture itself or any follow-up read. Goes false the moment the session
   falls quiet, including on a disconnect (drop-all! empties `pending`), so a
   spinner can never outlive the work it stands for."
  [session-id value]
  (let [{:keys [session] v :value} @latest-click
        sid (str session-id)]
    (boolean (and v (= v (str value)) (= session sid)
                  (or (waiting-on? value) (session-waiting? sid))))))
